import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query/react';

import { formatDateString } from '~libs/dayjs';

import { Button, ClickableIcon, ContainerHeader, ContainerState, Toolbar } from '~components';
import { faArrowLeft, faPieChart, faRefresh } from '~components/icon';

import { setTitle } from '~main/actions/login';
import { useGetBudgetTrackerQuery } from '~support-at-home/actions/budgetTracker';

import BudgetBreakdown from './BudgetBreakdown';
import CaseManagementOverview from './CaseManagementOverview';
import Overview from './Overview';
import Summary from './Summary';
import { TRACKER_TEXT } from './trackerText';
import { buildTrackerView } from './transforms';

const BudgetTracker: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const queryArgs = useMemo<{ id: string } | typeof skipToken>(() => {
    return id ? { id } : skipToken;
  }, [id]);

  const { data, isFetching, error, refetch } = useGetBudgetTrackerQuery(queryArgs, {
    refetchOnMountOrArgChange: true,
  });

  const view = useMemo(() => (data ? buildTrackerView(data) : null), [data]);

  const reference = data?.reference;

  // The tab title names the budget once it is known, e.g. "BD-PDS-O Budget Tracker - Turnpoint Assist"
  useEffect(() => {
    if (reference) {
      setTitle(`${reference} Budget Tracker`);
    }
  }, [reference]);

  const onBack = () => {
    navigate(`/support-at-home/budgets`);
  };

  const onOpenBudget = () => {
    navigate(`/support-at-home/budgets/${id}`);
  };

  const renderContent = () => {
    // First load and every refresh: the whole tracker is worked out again, so it is replaced by the spinner
    if (isFetching) {
      return <ContainerState loadingDataCy="budget-tracker-loading-spinner" />;
    }

    if (error) {
      return <div className="alert alert-danger">Error loading budget tracker data.</div>;
    }

    if (!data || !view) {
      return <div className="alert alert-warning">No budget data available for ID: {id}</div>;
    }

    const { client, start = '', end = '', case_management, funding_type } = data;

    const description = `${client?.full_name || ''}`;
    const subDescription = start && end ? `${formatDateString(start)} to ${formatDateString(end)}` : '';

    return (
      <>
        <Summary
          title="Budget Summary"
          description={description}
          subDescription={subDescription}
          fundingType={funding_type?.name}
          status={data.status}
          hcpUnspentFunds={data.hcp_unspent_funds}
          summary={view.summary}
        />
        <div className="figure-overview-row">
          <Overview data={view.overview} />
          <CaseManagementOverview data={case_management} />
        </div>
        <BudgetBreakdown data={view.breakdown} />
      </>
    );
  };

  return (
    <div className="budget-tracker-container">
      <ContainerHeader
        icon={faPieChart}
        iconSize="1x"
        className="budget-tracker-header info"
        dataCy="budget-tracker-header"
      >
        {reference ? `Budget Tracker - ${reference}` : 'Budget Tracker'}
      </ContainerHeader>
      <Toolbar
        id="budget-tracker-toolbar"
        leftCluster={
          <ClickableIcon
            icon={faArrowLeft}
            id="budget-tracker-back-button"
            onClick={onBack}
            toolTip="Back to Budgets"
            ariaLabel="Back to Budgets"
          />
        }
        rightCluster={
          <>
            {/* Fetches the tracker again and redraws every section, without reloading the browser page */}
            <ClickableIcon
              icon={faRefresh}
              id="budget-tracker-refresh-button"
              onClick={() => refetch()}
              disabled={!id || isFetching}
              toolTip="Refresh"
              ariaLabel="Refresh budget tracker"
              dataCy="budget-tracker-refresh-button"
            />
            <Button
              size="sm"
              color="success"
              onClick={onOpenBudget}
              disabled={!id}
              dataCy="budget-tracker-open-budget-button"
            >
              {TRACKER_TEXT.toolbar.openBudget}
            </Button>
          </>
        }
      />
      <div className="budget-tracker-content">{renderContent()}</div>
    </div>
  );
};

export default BudgetTracker;
